import angular from 'angular';

/*******************************************************************************
 * ReturnItemRunGridItem */
/**
 * Creates a data object constructor by setting default values and functions on
 * the prototype. The data will be initialized from the `source`.
 *******************************************************************************/
/* @ngInject */ export function ReturnItemRunGridItem(
  $sce,
  Data,
  SERVER,
  Tools
) {
  /*-------------------------------------------------------------------*/
  /* We need to keep track of a temporary id as there are some cases   */
  /* when we need to set the parentID of one object to the temporaryID */
  /* of another object in the same return. We use a negative number so */
  /* that the server will recognize that as a temporary number, and    */
  /* match it to a proper number after saving to the database.         */
  /*-------------------------------------------------------------------*/
  var mTemporaryID = 0;

  /***************************************************************************
   * Constructor
   ***************************************************************************/
  Data.ReturnItemGridItem = function (source) {
    /*-------------------------------------------------------------------*/
    /* An internal receivable payment (cash/check paid directly against  */
    /* a fine or charge) is reversed via a LIT_RECEIVABLE_CREDIT item    */
    /* with SubType LIS_REFUND (ReceivableRefund - see the Java POS      */
    /* precedent), not a LIT_ITEM_RETURN. That item needs to reference   */
    /* the ORIGINAL charge/fine the payment paid down - the payment's    */
    /* own `referenceItemID` - not the payment's own id like every other */
    /* source type here (where "the thing being returned" IS the source */
    /* item itself).                                                     */
    /*-------------------------------------------------------------------*/
    var isReceivablePayment =
      source.type === SERVER.BooksDBConstants.LIT_RECEIVABLE_CREDIT &&
      (source.subType === SERVER.BooksDBConstants.LIS_ITEM_PAYMENT ||
        source.subType === SERVER.BooksDBConstants.LIS_FINE_PAYMENT);

    var returnItem = {
      id: --mTemporaryID,
      subType: isReceivablePayment
        ? SERVER.BooksDBConstants.LIS_REFUND
        : source.subType,
      referenceItemID: isReceivablePayment ? source.referenceItemID : source.id,
      /*-------------------------------------------------------------------*/
      /* The ORIGINAL PAYMENT's own id (see ReceivableRefund.cs's          */
      /* sourcePaymentID) - required for a receivable payment reversal, so */
      /* a single refund can be capped at that specific payment's own      */
      /* remaining amount, not just the group's overall balance. The      */
      /* server durably records this into books.ReceivableRefundSource     */
      /* once saved - it's not just a validation-time-only hint anymore.  */
      /*-------------------------------------------------------------------*/
      sourcePaymentID: isReceivablePayment ? source.id : null,
      memo: source.memo,
      entityID: source.entityID,
      classID: source.classID,
      inventoryProductID: source.inventoryProductID,
      discountAcctID: source.discountAcctID
    };

    var rigi = Object.assign(
      Object.create(Data.ReturnItemGridItem.prototype),
      returnItem
    );
    rigi.source = source;

    return rigi;
  };

  /***************************************************************************
   * Prototype
   ***************************************************************************/
  Data.ReturnItemGridItem.prototype = {
    /*------------------------------------*/
    /* Protected properties. These should */
    /* only be used in this instance.     */
    /*------------------------------------*/
    mCachedOptions: null,
    mCachedHtmlContent: null,

    classID: null,
    amountItemReturnAmount: null,
    discountAcctID: null,
    entityID: null,
    inventoryProductID: null,
    memo: null,
    newQty: 0,
    referenceItemID: null,
    source: null,
    taxable: false,
    taxRate: 0,

    /*-----------*/
    /* Accessors */
    /*-----------*/
    get accountID() {
      return this.amountItemReturnAmount ? this.source.accountID : 0;
    },
    get balanceDue() {
      /*-------------------------------------------------------------*/
      /* "Balance due" isn't a meaningful concept for a receivable   */
      /* payment reversal row - there's no remaining amount owed to  */
      /* reduce, just a flat payment amount to undo - and returning  */
      /* 0 here keeps saveReturn()'s `rigi.balanceDue > 0` check from */
      /* trying to attach a getCreditMemo() to it. This row is itself */
      /* the receivable-side effect (ReceivableRefund/LIS_REFUND);   */
      /* no separate credit memo is needed.                          */
      /*-------------------------------------------------------------*/
      return this.isReceivablePayment
        ? 0
        : Tools.roundMoney(this.source.totalPrice - this.source.totalPaid);
    },
    get gasbCode() {
      return this.amountItemReturnAmount ? this.source.gasbCode : null;
    },
    get hasOptions() {
      return (this.source.options || []).length;
    },
    get hasReturn() {
      return !!(this.returnQuantity || this.returnAmount);
    },
    get isFullReturn() {
      return this.source.qtyOrdered === this.returnQuantity;
    },
    get itemID() {
      return this.source.itemID;
    },
    get itemName() {
      return `${this.memo}${this.name ? ' For: ' : ''}${this.name}`;
    },
    get items() {
      return this.source.items;
    },
    get isConvenienceFee() {
      return (
        this.source.type === SERVER.BooksDBConstants.LIT_SERVICE_FEE_CREDIT
      );
    },
    get isDonation() {
      return this.source.type === SERVER.BooksDBConstants.LIT_DONATION_PURCHASE;
    },
    get isStudentFunds() {
      return (
        this.source.type === SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_PURCHASE
      );
    },
    get isReceivablePayment() {
      return (
        this.source.type === SERVER.BooksDBConstants.LIT_RECEIVABLE_CREDIT &&
        (this.source.subType === SERVER.BooksDBConstants.LIS_ITEM_PAYMENT ||
          this.source.subType === SERVER.BooksDBConstants.LIS_FINE_PAYMENT)
      );
    },
    get isAmountItem() {
      return (
        this.isConvenienceFee ||
        this.isStudentFunds ||
        this.isDonation ||
        this.isReceivablePayment
      );
    },
    get name() {
      return this.source.name ? this.source.name : '';
    },
    get price() {
      return this.isAmountItem ? null : this.source.price;
    },

    /**
     * In the case where the ordered item has an option attached to it, we want to get the sum of each options qtyChange value
     * and subtract it from the qtyOrdered (which is the sum of qtyChange plus the quantity of the item itself). This will allow
     * the correct quantity to appear on the grid for the user.
     * These are the edge cases it covers:
     *  - If the item has an option, but it was never set and the sale was made, the this.source.options will be a
     *    empty array (the reduce will return a zero) and it will simply use this.source.qtyOrdered value.
     *  - If a "Order" is made and 1 or more of the item is delivered, this.source.options will be a
     *    empty array (the reduce will return a zero), and it will simply use this.source.qtyOrdered value.
     *  - There are code in place for the situation where the item has a quantity of more than 1, it will not allow
     *    the user to add options, therefore resulting in this.source.options being an empty array. And for situations
     *    the system does allow the user to have options for more than one item the reduce will calculate the sum.
     */
    get quantity() {
      return this.isAmountItem
        ? null
        : this.source.qtyOrdered -
            this.source.options.reduce(
              (total, option) => (total += option.qtyChange),
              0
            );
    },
    get type() {
      if (this.isDonation) return SERVER.BooksDBConstants.LIT_DONATION_RETURN;
      if (this.isStudentFunds)
        return SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_RETURN;
      if (this.isConvenienceFee)
        return SERVER.BooksDBConstants.LIT_SERVICE_FEE_CREDIT;
      if (this.isReceivablePayment)
        return SERVER.BooksDBConstants.LIT_RECEIVABLE_CREDIT;
      else return SERVER.BooksDBConstants.LIT_ITEM_RETURN;
    },
    /**
     * Originally the amount = this.source.amount, however that did not work because that amount is the original receipt amount. What we need
     * is the total amount of the order based on what items have not already been returned. Before the total of the receipt would show incorrectly
     * if parts of the receipt had already been returned. Furthermore, we use the variable quantity instead of qtyOrdered for the cases where
     * the ordered item has an option attached to it we need to ensure that the value is calculating based off the quantity we are displaying
     * to the user.
     *
     * For Convenience fees (SERVICE_FEE_CREDIT), we want to just take the amount from the source.
     *
     * This is just a visual change for the column Total for Return Receipts section.
     */
    get amount() {
      if (this.isAmountItem)
        /*-------------------------------------------------------------*/
        /* GR-2375: cap at what's actually still reversible, not the   */
        /* original full amount - see returnItems.js's grid filter for */
        /* why this field exists. Populated for all four amount-only   */
        /* types (receivable payment, convenience fee, student funds   */
        /* purchase, donation) by Receipts.cs.get().                   */
        /*-------------------------------------------------------------*/
        return this.source.remainingReversibleAmount;

      return this.price * this.quantity + this.tax;
    },

    /*************************************************************************
     * alreadyReversedAmount */
    /**
     * How much of a physical item's dollar value has already been given
     * back some other way - most commonly an NSF Check's revenue-only
     * reversal, which doesn't touch quantity, so it wouldn't otherwise show
     * up on this row. Purely informational; it doesn't cap or change how a
     * physical return's own amount is computed (ItemReturn.bookResults
     * already recalculates server-side regardless), it just surfaces that
     * the balance isn't what price x quantity alone would suggest.
     *
     * Gated to !isAmountItem since the flat-amount types already show this
     * same effect via the capped `amount` getter above.
     *************************************************************************/
    get alreadyReversedAmount() {
      return this.isAmountItem ? 0 : this.source.returnedAmount || 0;
    },

    get returnAmount() {
      return this.isAmountItem
        ? Math.min(this.amount, this.amountItemReturnAmount)
        : Tools.roundMoney(this.subTotal + this.returnTax);
    },
    set returnAmount(value) {
      this.amountItemReturnAmount = this.isAmountItem
        ? Tools.roundMoney(value)
        : null;
    },
    get returnTax() {
      return this.isAmountItem
        ? null
        : Tools.roundMoney(
            this.subTotal * (this.source.taxable ? this.source.taxRate : 0)
          );
    },
    get subTotal() {
      return this.isAmountItem ? null : this.returnQuantity * this.price;
    },
    get tax() {
      return this.isAmountItem ? null : this.source.tax;
    },

    /*************************************************************************
     * tooltipHtml */
    /**
     * Html Tooltip text for options. This is to be used with the
     * uibTooltipHtml directive. This accssor will manage its own caching of
     * values so an infinite digest cycle error will be prevented. This has to
     * be done this way as there is no scope available at this level.
     *************************************************************************/
    get tooltipHtml() {
      if (!angular.equals(this.mCachedOptions, this.source.options)) {
        /*----------------------------------*/
        /* A new array reference is used so */
        /* element equality can be checked. */
        /*----------------------------------*/
        this.mCachedOptions = this.source.options.concat();

        if (this.mCachedOptions.length)
          this.mCachedHtmlContent = $sce.trustAsHtml(
            this.source.options
              .map(function (lio) {
                return '<b>{0}</b>: {1}'.format(
                  lio.optionMasterName,
                  lio.optionValue
                );
              })
              .join('<br/>')
          );
        else this.mCachedHtmlContent = null;
      }

      return this.mCachedHtmlContent;
    },

    /*************************************************************************
     * Property: returnQuantity */
    /**
     * Accessor for the quantity on the item return. The setter will handle any
     * validation against the original quantity sold. Convenience Fees don't
     * have a quantity, so return null so it shows as blank.
     *************************************************************************/
    get returnQuantity() {
      return this.isAmountItem ? null : this.newQty || 0;
    },
    set returnQuantity(value) {
      /*--------------------------------------------------------*/
      /* Check to make sure the return quantity isn't more than */
      /* what has been sold and is a valid quantity to return.  */
      /*--------------------------------------------------------*/
      if (value > this.quantity || value < 0) return;

      /*------------------------------------------*/
      /* Update the return quantity and discount. */
      /*------------------------------------------*/
      this.newQty = value;
      this.backordered = this.getBackOrdered(this.source);

      /*------------------------------------*/
      /* If there are any sub items, update */
      /* the quantities and recalculate.    */
      /*------------------------------------*/
      if (this.items)
        this.items.forEach(function (si) {
          si.backordered = this.getBackOrdered(si);
        });
    },

    /*************************************************************************
     * getBackOrdered */
    /**
     * Returns the back ordered quantity that is possible from the return
     * quantity and the provided ledger item. Since this is for a void, the
     * value will be negative.
     *
     * @param   li  Ledger Item to check for backordered quantity
     * @returns     Negative quantity to back order.
     *************************************************************************/
    getBackOrdered: function (li) {
      return -Math.min(li.qtyOrdered - li.qtyDelivered, this.returnQuantity);
    },

    /*************************************************************************
     * getCreditMemo */
    /**
     * The purpose of this is to reduce the amount that the customer owes the School.
     *
     * Returns the corresponding <b>ReceiveableItemCreditMemo</b> for this return.
     * It is important to understand that this creates a "return" for each row item in
     * the DB that is being returned by the user. For example:
     *
     * Sally orders: 3 Gateway Yearbooks, 2 Yearbooks, Cash Payment towards them of $20  ($10 applied to each) .
     *
     * ------------------------------------------------------------------------------------------------------
     * Note: Why does the payment matter? We only create Receivables if the order has not been paid of yet.
     *
     * What is a Receivable? A Receivable are either Debits or Credits. For example Receivable Debit means
     * the customer owes the school money, then when this money is paid a Receivable Credit is generated to balance.
     * ------------------------------------------------------------------------------------------------------
     *
     * -- System creates --
     * Cash Payment (1 DB Row) - if the user who input this order actually created a payment
     *   MONEY_RECEIVED
     *
     * Gateway Yearbooks (2 DB Rows)
     *   ITEM SALE -> qty 3
     *   RECEIVABLE_DEBIT -> qty null (Circumstantially, see note above)
     *
     * Yearbooks (2 DB Rows)
     *   ITEM SALE -> qty 2
     *   RECEIVABLE_DEBIT -> qty null (Circumstantially, see note above)
     *
     * Now lets do a return:
     * Sally returns: 2 Gateway Yearbooks, 1 Yearbook, No cash returned
     *
     * -- System creates --
     *
     * Gateway Yearbooks (2 DB Rows)
     *   ITEM_RETURN -> qty -2
     *   RECEIVABLE_CREDIT -> qty null (Circumstantially, see note above)
     *
     * Yearbooks (2 DB Rows)
     *   ITEM_RETURN -> qty -1
     *   RECEIVABLE_CREDIT -> qty null (Circumstantially, see note above)
     *
     * For any sort of balancing and seeing how much customers owe the system only cares
     * about RECEIVABLE_CREDIT and RECEIVABLE_DEBIT.
     *
     * @returns  Receivable credit to reduce owed amount.
     *************************************************************************/
    getCreditMemo: function () {
      return this.balanceDue === 0
        ? null
        : {
            type: SERVER.BooksDBConstants.LIT_RECEIVABLE_CREDIT,
            subType: SERVER.BooksDBConstants.LIS_ITEM_CREDIT_MEMO,
            referenceItemID: this.referenceItemID,
            /**
             * This is an extremely important piece of code to change, this controls how much the system will register the return is worth against the
             * original order. In the past this line used to be this.balanceDue (balanceDue = this.totalPrice - this.totalPaid).
             *
             * The balanceDue field is the calculation of what the customer owes the school for that row/item in our DB specifically. This does not
             * take into account when 1 row has a quantity higher than 1. The user can return 1 of those instead of all of them however we only keep 1 row in DB. So
             * for instance if a user buys 2 yearbooks initially we will only have 1 row in db with a qty of 2. That creates an issue when using balanceDue since balanceDue is the
             * amount due for all of the items represented by the row in the DB. Take this example below:
             *
             * Barry buys 2 yearbooks at $80.
             * Barry puts a cash payment of $20 down and then he will get the rest of the money to the school later.
             *
             * 1. Our system will create 1 row that houses the info: yearbook, qty: 2, price: 80, total: 160, balanceDue: 140
             *
             * Now Barry needs to return 1 yearbook.
             *
             * Before when the system would use this fn to create the return it would take the items balanceDue and just set that to the amount. This works except for when you
             * only return 1 of items on the same db row.
             *
             * Barry returns 1 yearbook - price: 80, balanceDue: 140
             *
             * Since balanceDue is calculated for all items within the row (db rows with qty > 1) we cannot use it for the Return's amount value.
             *
             * So instead I opted to go for using the price which is the items actual price and then multiplying it by the number of returned items,
             * that way if the order has 3 and they return 2 it is still correct. Secondly the * -1 fixes the fact that this.price * this.newQty
             * will be a negative number since this.newQty is negative. Finally we compare this new calculated value to the the original balanceDue to see
             * which one is smaller. Why?
             *
             * The amount of the return can never make the school owe the customer.
             *
             * The calculated value does not account for the payment made against the receipt originally but balanceDue does. So to insure we are not reducing more then what
             * is owed we use the balanceDue which will be smaller in this case.
             *
             * The calculated value is the returned items' price plus their tax (returnAmount), not price * qty alone. balanceDue includes tax, so leaving
             * it out left the tax still owed: returning 2 of 5 taxed $10 shirts on account credited $20 of a $21.65 return, and Unpaid Items showed $1.65
             * more than the 3 shirts kept. It's also the same per-row amount balanceReduction (returnItems.js) takes off what's due.
             */
            amount: Math.min(this.balanceDue, Math.abs(this.returnAmount)),
            memo: `Return of ${this.memo}`
          };
    },

    /*************************************************************************
     * toJSON */
    /**
     * Since all the properties on the prototype may not be serialized, we want
     * the object to contain a direct representation of the properties when it
     * is converted to JSON for the server.
     *************************************************************************/
    toJSON: function () {
      var result = {};
      for (var key in this) result[key] = this[key];

      /*--------------------------------*/
      /* Replace properties from source */
      /* with those for actual return.  */
      /*--------------------------------*/
      result.quantity = this.returnQuantity || 0;
      result.tax = this.returnTax;
      result.amount = this.returnAmount;
      result.subTotal = this.subTotal || 0;
      result.tax = this.tax || 0;
      return result;
    }
  };
}
